import {
  Box,
  ButtonBase,
  Drawer,
  Stack,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Pressable in the guided-course idiom: a hard offset shadow instead of a
// blur, so the button reads as a physical key that depresses on click. Same
// vocabulary as the course CTAs (`0 Npx 0 <deep>`), in the store's own navy.
export const NavigateButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: 'cart' | 'orders' }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  padding: '11px 20px',
  borderRadius: '14px',
  fontWeight: 700,
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: `0 4px 0 ${theme.palette.primary.main}`,
  transition: 'transform .12s ease, box-shadow .12s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    transform: 'translateY(-1px)',
    boxShadow: `0 5px 0 ${theme.palette.primary.main}`,
  },
  '&:active': {
    transform: 'translateY(4px)',
    boxShadow: `0 0 0 ${theme.palette.primary.main}`,
  },
}));

export const GemsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  borderRadius: '5px',
  backgroundColor: theme.palette.secondary.main,
  padding: '2px 8px 2px 5px',
  width: 'fit-content',
}));

export const StyledQuantityButton = styled(ButtonBase)(
  ({ disabled, theme }) => ({
    border: `1.5px solid ${theme.palette.primary.main}`,
    borderRadius: '5px',
    width: '20px',
    height: '20px',
    '& .MuiTypography-root': {
      color: disabled
        ? theme.palette.action.disabled
        : theme.palette.primary.main,
    },
    ':disabled': {
      cursor: 'not-allowed',
      border: `1.5px solid ${theme.palette.action.disabled}`,
    },
  }),
);

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  width: '100%',
  boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.25)',
}));

export const StyledTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  '& .MuiTableHead-root': {
    '& .MuiTableRow-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTableCell-head': {
      backgroundColor: theme.palette.background.paper,
      padding: '20px',
      borderRadius: '16px',
      border: 'none',
      textWrap: 'nowrap',
      '&:not(:first-of-type)': {
        textAlign: 'center',
      },
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '& .MuiTableCell-root': {
        padding: '12px 20px',
        color: theme.palette.text.primary,
        fontWeight: '500',
        '&:not(:first-of-type)': {
          textAlign: 'center',
        },
        border: 'none',
      },
    },
  },
}));

export const SegmentedControl = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '4px',
  padding: '4px',
  margin: '20px 0',
  borderRadius: '14px',
  backgroundColor: '#E6EAF0',
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  }),
}));

export const SegmentButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active, theme }) => ({
  flex: 1,
  gap: '8px',
  padding: '9px 12px',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: active ? 700 : 500,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.background.paper : 'transparent',
  boxShadow: active ? '0 2px 6px rgba(16, 24, 40, 0.12)' : 'none',
  transition:
    'color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
  ...theme.applyStyles('dark', {
    boxShadow: active ? '0 2px 6px rgba(0, 0, 0, 0.5)' : 'none',
  }),
}));

export const StoreDrawer = styled(Drawer)({
  bottom: 'calc(var(--bottom-nav-h) - 2px)',
  '& .MuiModal-backdrop': {
    bottom: 'calc(var(--bottom-nav-h) - 2px)',
  },
  '& .MuiDrawer-paper': {
    borderRadius: '20px 20px 0px 0px',
    padding: '24px 16px 40px 16px',
    boxShadow: 'none',
  },
  zIndex: 1400,
});

export const StoreMenuButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active, theme }) => ({
  width: '100%',
  textAlign: 'left',
  display: 'flex',
  justifyContent: 'flex-start',
  padding: '10px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: '400',
  color: theme.palette.text.primary,
  backgroundColor: active ? '#EBF3FD' : 'none',
  '&:hover': {
    backgroundColor: '#EBF3FD',
  },
}));

export const ScrollableContainer = styled(Stack)({
  gap: '16px',
  overflow: 'auto',
  padding: '2px',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '&::-webkit-scrollbar-thumb': {
    display: 'none',
  },
  '&::-webkit-scrollbar-track': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
});

export const RazorpayHighlightContainer = styled(Stack)({
  marginTop: '10px',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '5px',
});

/**
 * The Razorpay wordmark, legible on either surface.
 *
 * The asset is a single raster of near-black navy with a blue glyph — on the
 * dark checkout panel the wordmark all but disappears and only the glyph shows.
 * Razorpay has no light asset in this repo, so dark mode renders the whole mark
 * in white: the reverse treatment a mono logo is meant to get, rather than a
 * recolour that would leave it half-legible.
 */
export const RazorpayMark = styled('img')(({ theme }) => ({
  height: '18px',
  display: 'block',
  ...theme.applyStyles('dark', {
    filter: 'brightness(0) invert(1)',
  }),
}));

export const OrderContainerMobile = styled(Stack)({
  padding: '12px',
  boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.25)',
  borderRadius: '12px',
  flexDirection: 'row',
  justifyContent: 'space-between',
});

export const QuantityContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  right: -3,
  bottom: -3,
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  width: '20px',
  height: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '16px',
    height: '16px',
    right: -5,
    bottom: -5,
  },
}));

export const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'hasPrice',
})<{
  hasPrice: boolean;
}>(({ hasPrice, theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '18px',
  borderRight: hasPrice ? `1px solid ${theme.palette.divider}` : 'none',
  paddingRight: hasPrice ? '4px' : '0',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
  },
}));

/* ---- Store shell: the guided-course look, applied to the page itself ---- */

// The store used to sit on flat grey. This is the same warm-to-cool wash the
// course pages use, with two soft light sources so the cards have something to
// sit on rather than float against a slab.
export const StorePageBackdrop = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: 1,
  background: `
    radial-gradient(900px 420px at 12% -8%, ${theme.palette.guidedCourse.goldTint} 0%, transparent 62%),
    radial-gradient(780px 380px at 92% 4%, ${theme.palette.guidedCourse.blueTint} 0%, transparent 58%),
    ${theme.palette.guidedCourse.bg}
  `,
  ...theme.applyStyles('dark', {
    background: `
      radial-gradient(900px 420px at 12% -8%, rgba(255,176,32,.10) 0%, transparent 62%),
      radial-gradient(780px 380px at 92% 4%, rgba(47,116,208,.14) 0%, transparent 58%),
      ${theme.palette.background.default}
    `,
  }),
}));

// Small gold pill above a section title — the course pages' eyebrow, which is
// what makes a heading read as part of a game rather than a table of contents.
export const SectionEyebrow = styled(Box)(({ theme }) => {
  const gc = theme.palette.guidedCourse;
  return {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 12px',
    borderRadius: '100px',
    fontFamily: gc.fontBody,
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.brand.goldInk,
    backgroundColor: gc.goldTint,
    border: `1px solid ${gc.goldTintBorder}`,
  };
});

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: theme.palette.guidedCourse.fontDisplay,
  fontSize: '30px',
  fontWeight: 800,
  lineHeight: 1.1,
  letterSpacing: '-0.01em',
  color: theme.palette.guidedCourse.ink,
  ...theme.applyStyles('dark', { color: theme.palette.text.primary }),
}));

export const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: theme.palette.guidedCourse.fontBody,
  fontSize: '14.5px',
  color: theme.palette.guidedCourse.ink2,
  ...theme.applyStyles('dark', { color: theme.palette.text.secondary }),
}));
